import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, ExternalLink, Smartphone } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { Button, ErrorState, LoadingState } from "../../components/Ui";
import { getProduct } from "../../lib/api";
import {
  applyPageMetadata,
  buildProductStructuredData,
  DEFAULT_OG_IMAGE,
  DEFAULT_SEARCH_IMAGE,
} from "../../lib/seo";
import { ProductGallery } from "./ProductGallery";

export function ProductDetailPage() {
  const { slug = "" } = useParams();
  const query = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
    enabled: Boolean(slug),
  });

  useEffect(() => {
    if (query.isError) {
      applyPageMetadata({
        title: "Product Not Found | Steria House",
        description: "The requested Steria House product could not be found.",
        canonicalPath: `/projects/${slug}`,
        noindex: true,
      });
      return;
    }
    if (!query.data) return;
    const product = query.data;
    applyPageMetadata({
      title: `${product.name} | Steria House`,
      description: product.shortDescription,
      canonicalPath: `/projects/${product.slug}`,
      image: DEFAULT_OG_IMAGE,
      imageAlt: `${product.name} by Steria House`,
      structuredData: buildProductStructuredData({
        name: product.name,
        slug: product.slug,
        description: product.shortDescription,
        category: product.category,
        platform: product.platform,
        image: product.images[0]?.imageUrl || DEFAULT_SEARCH_IMAGE,
        websiteUrl: product.websiteUrl,
      }),
    });
  }, [query.data, query.isError, slug]);

  if (query.isLoading) return <LoadingState label="Loading product" />;
  if (query.isError || !query.data) {
    return <ErrorState title="Product not found" message="This product may be unavailable or inactive." />;
  }

  const product = query.data;
  return (
    <div className="detail-page">
      <header className="detail-nav">
        <Brand />
        <Link to="/#products">
          <ArrowLeft size={16} /> All products
        </Link>
      </header>
      <main className="detail-main">
        <div className="detail-hero">
          <div className="detail-title">
            <div className="product-labels">
              <span>{product.category}</span>
              <span>{product.platform}</span>
            </div>
            <h1>{product.name}</h1>
            <p>{product.shortDescription}</p>
            <div className="detail-actions">
              {product.websiteUrl && (
                <Button href={product.websiteUrl}>
                  Visit website <ExternalLink size={16} />
                </Button>
              )}
              {product.playStoreUrl && (
                <Button href={product.playStoreUrl} variant="secondary">
                  Google Play <Smartphone size={16} />
                </Button>
              )}
              {product.appStoreUrl && (
                <Button href={product.appStoreUrl} variant="secondary">
                  App Store <ArrowUpRight size={16} />
                </Button>
              )}
            </div>
          </div>
          <div className="detail-index">SH / {String(product.displayOrder).padStart(2, "0")}</div>
        </div>
        <ProductGallery images={product.images} productName={product.name} />
        <section className="detail-about">
          <span>ABOUT THE PRODUCT</span>
          <p>{product.longDescription}</p>
        </section>
      </main>
    </div>
  );
}
